// ==================== CHECKOUT PAGE ====================

import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { loadStripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from '@stripe/react-stripe-js';
import {
  ArrowLeft,
  Shield,
  Lock,
  CreditCard,
  CheckCircle,
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Logo } from '../../components/ui/Logo';
import { fadeInUp } from '../../utils/animations';
import type { PriceCalculation } from '../../services/subscription.service';

// Initialize Stripe with publishable key from environment
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || '');

// ==================== PRICE SUMMARY SIDEBAR ====================

interface PriceSummarySidebarProps {
  priceDetails: PriceCalculation | null;
}

const PriceSummarySidebar = ({ priceDetails }: PriceSummarySidebarProps) => {
  if (!priceDetails) return null;

  return (
    <div className="bg-(--color-navy-light) border border-(--color-border-gray) rounded-2xl p-6">
      <h2 className="text-lg font-semibold text-white mb-6">Order Summary</h2>

      <div className="space-y-4">
        {/* Plan */}
        <div className="flex justify-between items-start">
          <div>
            <p className="text-white font-medium">{priceDetails.planName} Plan</p>
            <p className="text-sm text-(--color-muted-gray)">
              {priceDetails.billingCycle === 'YEARLY' ? 'Annual' : 'Monthly'} billing
              {(priceDetails.organizationCount ?? 1) > 1 && ` · ${priceDetails.organizationCount} accounts`}
            </p>
          </div>
          <span className="text-white font-medium">
            ${priceDetails.planAmount.toFixed(2)}
          </span>
        </div>

        {/* Setup Fee */}
        {priceDetails.setupFeeIncluded && priceDetails.setupFee > 0 && (
          <div className="flex justify-between items-start">
            <div>
              <p className="text-white font-medium">WhatsApp Setup</p>
              <p className="text-sm text-(--color-muted-gray)">
                One-time fee
                {(priceDetails.organizationCount ?? 1) > 1 && ` · ${priceDetails.organizationCount} accounts`}
              </p>
            </div>
            <span className="text-white font-medium">
              ${priceDetails.setupFee.toFixed(2)}
            </span>
          </div>
        )}

        {priceDetails.leadGenAddonIncluded && priceDetails.leadGenAddon > 0 && (
          <div className="flex justify-between items-start">
            <div>
              <p className="text-white font-medium">Lead Generation Agent</p>
              <p className="text-sm text-(--color-muted-gray)">One-time add-on (Lead Ops)</p>
            </div>
            <span className="text-white font-medium">
              ${priceDetails.leadGenAddon.toFixed(2)}
            </span>
          </div>
        )}

        {!!priceDetails.discountAmount && (
          <div className="flex justify-between text-sm text-(--color-success)">
            <span>Discount ({priceDetails.coupon?.code})</span>
            <span>−${Number(priceDetails.discountAmount).toFixed(2)}</span>
          </div>
        )}
        {/* Divider */}
        <div className="border-t border-(--color-border-gray) pt-4">
          <div className="flex justify-between items-center">
            <span className="text-lg font-semibold text-white">{priceDetails.coupon ? 'Total Due Today' : 'Total'}</span>
            <span className="text-2xl font-bold text-(--color-signal-orange)">
              ${Number(priceDetails.amountDueNow ?? priceDetails.totalAmount).toFixed(2)}
            </span>
          </div>
          <p className="text-xs text-(--color-muted-gray) mt-2">
            {priceDetails.currency.toUpperCase()} • 
            Then ${Number(priceDetails.nextRenewalAmount ?? priceDetails.recurringAmount).toFixed(2)}/{priceDetails.billingCycle === 'YEARLY' ? 'year' : 'month'}
          </p>
        </div>
      </div>

      {/* Security Badges */}
      <div className="mt-8 pt-6 border-t border-(--color-border-gray)">
        <div className="flex items-center gap-2 text-(--color-success) mb-4">
          <Shield size={18} />
          <span className="text-sm font-medium">Secure Payment</span>
        </div>
        
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-(--color-muted-gray)">
            <Lock size={14} />
            <span>256-bit SSL encryption</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-(--color-muted-gray)">
            <CreditCard size={14} />
            <span>PCI DSS compliant</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-(--color-muted-gray)">
            <CheckCircle size={14} />
            <span>Cancel anytime</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN CHECKOUT PAGE ====================

interface LocationState {
  checkoutSessionId: string;
  clientSecret: string;
  subscriptionId: string;
  priceDetails: PriceCalculation;
}

const CheckoutPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const state = location.state as LocationState | null;
  const hasValidCheckoutData = Boolean(state?.clientSecret);

  useEffect(() => {
    // If no checkout data, redirect back to plan selection
    if (!hasValidCheckoutData) {
      navigate('/app/select-plan', { replace: true });
    }
  }, [hasValidCheckoutData, navigate]);

  const handleBack = () => {
    navigate('/app/select-plan');
  };

  if (!hasValidCheckoutData || !state) {
    return (
      <div className="min-h-screen bg-(--color-dark-navy) flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-(--color-signal-orange) border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-(--color-dark-navy)">
      {/* Header */}
      <header className="bg-(--color-midnight-navy)/95 backdrop-blur-xl border-b border-(--color-border-gray)">
        <div className="container-custom flex items-center justify-between h-18 py-3">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              leftIcon={<ArrowLeft size={16} />}
              className="text-(--color-muted-gray) hover:text-white"
            >
              Back
            </Button>
            <div className="w-px h-6 bg-(--color-border-gray)" />
            <Logo size="lg" />
          </div>
          
          <div className="flex items-center gap-2 text-(--color-success)">
            <Lock size={16} />
            <span className="text-sm font-medium hidden sm:inline">Secure Checkout</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container-custom py-12">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
          >
            {/* Page Title */}
            <div className="text-center mb-10">
              <h1 className="text-3xl font-heading font-bold text-white mb-2">
                Complete Your Payment
              </h1>
              <p className="text-(--color-soft-gray)">
                Enter your payment details to activate your subscription
              </p>
            </div>

            {/* Checkout Grid */}
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Stripe Embedded Checkout */}
              <div className="lg:col-span-2">
                <div className="bg-(--color-navy-light) border border-(--color-border-gray) rounded-2xl p-6 min-h-[500px]">
                  <EmbeddedCheckoutProvider
                    stripe={stripePromise}
                    options={{ clientSecret: state.clientSecret }}
                  >
                    <EmbeddedCheckout />
                  </EmbeddedCheckoutProvider>
                </div>
              </div>

              {/* Order Summary Sidebar */}
              <div className="lg:col-span-1">
                <PriceSummarySidebar priceDetails={state.priceDetails} />
              </div>
            </div>
          </motion.div>
        </div>
      </main>
    </div>
  );
};

export { CheckoutPage };

